import { ResolvedMatch, Employee } from '../interfaces/interface';
import { DateTime } from 'luxon';

/**
 * Escapes special characters in CSV fields
 * Wraps fields containing commas, quotes, or newlines in double quotes
 */
const escapeCsvField = (field: string | undefined | null): string => {
  if (field === undefined || field === null) {
    return '';
  }

  const stringField = String(field);

  // If field contains comma, quote, or newline, wrap in quotes and escape existing quotes
  if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
    return `"${stringField.replace(/"/g, '""')}"`;
  }

  return stringField;
};

/**
 * Generates CSV content from resolved attendees with employee and executive assistant data
 */
export const generateAttendeeHierarchyCsv = (resolvedAttendees: ResolvedMatch[]): string => {
  const headers = [
    'login_name',
    'employee_name',
    'employee_internal_email_address',
    'job_title_name',
    'location_city_name',
    'location_state_name',
    'job_level_name',
    'department_name_current',
    'ea_login_name',
    'ea_employee_name',
    'ea_email_address',
    'ea_job_title_name',
    'ea_location_city_name',
    'ea_location_state_name',
    'ea_job_level_name',
    'ea_department_name_current'
  ];

  const rows: string[] = [];

  rows.push(headers.join(','));

  resolvedAttendees.forEach((attendee) => {
    const employee = attendee.selectedEmployee as Employee | undefined;
    const executiveAssistant = attendee.selectedExecutiveAssistant as Employee | undefined;

    if (!employee) {
      return; // Skip attendees without selected employee
    }

    const row = [
      // Employee data
      escapeCsvField(employee.login_name?.toLowerCase()),
      escapeCsvField(employee.employee_name),
      escapeCsvField(employee.employee_internal_email_address),
      escapeCsvField(employee.job_title_name),
      escapeCsvField(employee.location_city_name),
      escapeCsvField(employee.location_state_name),
      escapeCsvField(employee.job_level_name),
      escapeCsvField(employee.department_name_current),
      // Executive Assistant data (if present)
      escapeCsvField(executiveAssistant?.login_name?.toLowerCase()),
      escapeCsvField(executiveAssistant?.employee_name),
      escapeCsvField(executiveAssistant?.employee_internal_email_address),
      escapeCsvField(executiveAssistant?.job_title_name),
      escapeCsvField(executiveAssistant?.location_city_name),
      escapeCsvField(executiveAssistant?.location_state_name),
      escapeCsvField(executiveAssistant?.job_level_name),
      escapeCsvField(executiveAssistant?.department_name_current)
    ];

    rows.push(row.join(','));
  });

  return rows.join('\n');
};

/**
 * Triggers browser download of CSV file
 */
export const downloadCsv = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/**
 * Main export function that generates and downloads attendee hierarchy CSV
 * Filename format: attendee-hierarchy-YYYY-MM-DD_HH-MM-SS.csv
 * Example: attendee-hierarchy-2025-10-08_00-06-07.csv
 * Uses local timezone for timestamp
 */
export const exportAttendeeHierarchyToCsv = (resolvedAttendees: ResolvedMatch[], onError?: (message: string) => void): void => {
  if (!resolvedAttendees || resolvedAttendees.length === 0) {
    console.warn('No attendees to export');
    return;
  }

  try {
    const csvContent = generateAttendeeHierarchyCsv(resolvedAttendees);

    // Generate timestamp in local timezone with format: YYYY-MM-DD_HH-MM-SS
    const timestamp = DateTime.now().toFormat('yyyy-MM-dd_HH-mm-ss');
    const filename = `attendee-hierarchy-${timestamp}.csv`;

    downloadCsv(csvContent, filename);
  } catch (error) {
    console.error('Failed to export CSV:', error);
    if (onError) {
      onError('Failed to download CSV file. Please try again or contact support if the issue persists.');
    }
  }
};
